// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Modal, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyInput from '@/components/GoffyInput';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { formatLastPlayed, useRecentCourses } from '@/hooks/useRecentCourses';
import { useCourseSearchMissReport } from '@/hooks/useCourseSearchMissReport';
import appColors from '@/theme/colors';
import { t } from '@/i18n';
import type { Course, RecentCourse } from '@/features/round-init/types';

type Props = {
  selectedCourseId: string | null;
  selectedCourseName: string;
  onSelect: (courseId: string, courseName: string, course: Course) => void;
  token: string | null | undefined;
  playerId: string | null | undefined;
};

/**
 * Self-contained course selector: fetches the selectable courses and the player's recently played
 * courses, renders a search field trigger plus a modal list, and reports the chosen course through
 * `onSelect`. Disabled courses are dropped before the picker ever sees them. Mirrors the inline
 * picker in round-init.tsx (which still owns its own copy for now — see TODO there).
 */
export default function CoursePicker({ selectedCourseId, selectedCourseName, onSelect, token, playerId }: Props) {
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(selectedCourseName);
  const recentCourses = useRecentCourses(playerId, token);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(t('course.error'));
        }
        const data = (await response.json()) as Course[];
        if (!cancelled) setCourses(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setError(t('course.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token]);

  // Keep the trigger text in sync with the selection while the modal is closed.
  useEffect(() => {
    if (pickerOpen) return;
    setSearchQuery(selectedCourseName);
  }, [pickerOpen, selectedCourseName]);

  // Disabled courses must not be selectable at all — drop them before the picker/search ever sees them.
  const selectableCourses = useMemo(
    () => courses.filter((course) => course.visibility !== 'disabled'),
    [courses]
  );

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return selectableCourses;
    return selectableCourses.filter((course) => {
      const name = course.name.toLowerCase();
      const address = (course.address ?? '').toLowerCase();
      return name.includes(query) || address.includes(query);
    });
  }, [searchQuery, selectableCourses]);

  const showRecentCourses = searchQuery.trim().length === 0 && recentCourses.length > 0;

  useCourseSearchMissReport({
    query: searchQuery,
    hasResults: filteredCourses.length > 0,
    active: pickerOpen && !loading && selectableCourses.length > 0,
  });

  /**
   * react-native-web's modal focus trap hands focus back to whatever was focused before the modal
   * opened -- here, the search field whose onFocus opened it -- and it does so when the modal
   * *unmounts*, which react-native-web defers to the end of the fade-out animation (~250 ms after
   * the close). Without this guard the picker would reopen a quarter of a second after every close.
   * The flag is cleared by the modal's own onDismiss, so it covers exactly that restored focus and
   * nothing else. Web only: native never restores focus, and Android has no onDismiss to clear it.
   */
  const suppressReopenRef = useRef(false);
  const triggerRef = useRef<TextInput>(null);
  const closePicker = useCallback(() => {
    suppressReopenRef.current = Platform.OS === 'web';
    setPickerOpen(false);
  }, []);
  const handlePickerDismissed = useCallback(() => {
    suppressReopenRef.current = false;
  }, []);

  const selectCourse = useCallback(
    (course: Course) => {
      onSelect(course.id, course.name, course);
      setSearchQuery(course.name);
      closePicker();
    },
    [closePicker, onSelect]
  );

  const selectRecentCourse = useCallback(
    (recent: RecentCourse) => {
      const match = courses.find((course) => course.id === recent.id);
      if (match) {
        selectCourse(match);
        return;
      }
      // Fall back to a minimal Course when the recent course isn't in the geodata list.
      onSelect(recent.id, recent.name, {
        id: recent.id,
        name: recent.name,
        address: recent.address,
        lat: 0,
        lon: 0,
      });
      setSearchQuery(recent.name);
      closePicker();
    },
    [closePicker, courses, onSelect, selectCourse]
  );

  const handleAddCourse = useCallback(() => {
    closePicker();
    router.push('/config-course?closeOnSave=1');
  }, [closePicker, router]);

  return (
    <View style={styles.courseCard}>
      <Text style={styles.courseLabel}>{t('schedule.pickCourse')}</Text>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {loading ? <ActivityIndicator /> : null}
      <TextInput
        ref={triggerRef}
        value={searchQuery}
        onChangeText={(value) => {
          setSearchQuery(value);
          setPickerOpen(true);
        }}
        onFocus={() => {
          if (suppressReopenRef.current) {
            // Drop the restored focus, so the next real tap produces a fresh focus event.
            triggerRef.current?.blur();
            return;
          }
          setPickerOpen(true);
        }}
        placeholder={t('players.courseSearchPlaceholder')}
        placeholderTextColor="#8FA08B"
        style={styles.courseSearchField}
        editable={courses.length > 0}
      />

      <Modal
        visible={pickerOpen}
        transparent
        animationType="fade"
        onRequestClose={closePicker}
        onDismiss={handlePickerDismissed}
      >
        <Pressable style={styles.modalBackdrop} onPress={closePicker}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Pressable style={styles.addCourseButton} onPress={handleAddCourse} accessibilityLabel={t('players.addCourse')}>
              <Text style={styles.addCourseButtonText}>{t('players.addCourse')}</Text>
            </Pressable>
            <GoffyInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder={t('players.courseSearchPlaceholder')}
              style={styles.courseSearchInput}
              autoCorrect={false}
              autoCapitalize="none"
              autoFocus
            />
            <Text style={styles.courseSearchHint}>{t('players.searchAnyCourseHint')}</Text>
            <FlatList
              data={filteredCourses}
              keyExtractor={(item) => item.id}
              keyboardShouldPersistTaps="handled"
              ListHeaderComponent={
                showRecentCourses ? (
                  <View>
                    <Text style={styles.recentSectionTitle}>
                      {t('players.recentlyPlayedTitle')}
                      <Text style={styles.recentSectionSubtitle}> · {t('players.recentlyPlayedSubtitle')}</Text>
                    </Text>
                    {recentCourses.map((course: RecentCourse) => (
                      <Pressable
                        key={course.lastRoundId || course.id}
                        style={[styles.recentRow, course.id === selectedCourseId && styles.courseOptionActive]}
                        onPress={() => selectRecentCourse(course)}
                      >
                        <View style={styles.recentRowText}>
                          <Text style={styles.courseOptionTitle}>{course.name}</Text>
                          {course.address ? <Text style={styles.courseOptionSubtitle}>{course.address}</Text> : null}
                        </View>
                        {formatLastPlayed(course) ? (
                          <Text style={styles.recentRowMeta}>{formatLastPlayed(course)}</Text>
                        ) : null}
                      </Pressable>
                    ))}
                    <View style={styles.pickerDividerRow}>
                      <View style={styles.pickerDividerLine} />
                      <Text style={styles.pickerDividerText}>{t('players.lookingForAnother')}</Text>
                      <View style={styles.pickerDividerLine} />
                    </View>
                    {filteredCourses.length > 0 ? (
                      <Text style={styles.nearbyLabel}>{t('players.nearbyCoursesTitle')}</Text>
                    ) : null}
                  </View>
                ) : null
              }
              renderItem={({ item }) => (
                <Pressable
                  style={[styles.courseOption, item.id === selectedCourseId && styles.courseOptionActive]}
                  onPress={() => selectCourse(item)}
                >
                  <Text style={styles.courseOptionTitle}>{item.name}</Text>
                  <Text style={styles.courseOptionSubtitle}>{item.address}</Text>
                  <Text
                    style={[
                      styles.courseBadge,
                      item.visibility === 'private'
                        ? styles.courseBadgePrivate
                        : item.visibility === 'pending'
                          ? styles.courseBadgePending
                          : styles.courseBadgeOfficial,
                    ]}
                  >
                    {item.visibility === 'private'
                      ? t('players.coursePrivateBadge')
                      : item.visibility === 'pending'
                        ? t('players.coursePendingBadge')
                        : t('players.courseOfficialBadge')}
                  </Text>
                </Pressable>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
              ListEmptyComponent={showRecentCourses ? null : <Text style={styles.emptyState}>{t('players.courseSearchEmpty')}</Text>}
            />
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  courseCard: {
    borderRadius: 16,
    padding: 18,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    zIndex: 0,
    gap: 10,
  },
  courseLabel: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
  },
  error: {
    color: '#ff6666',
    marginBottom: 8,
  },
  courseSearchInput: {
    marginBottom: 8,
  },
  courseSearchField: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#07200D',
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '600',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#07200D',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 12,
    maxHeight: '70%',
  },
  courseSearchHint: {
    color: '#7FA978',
    fontSize: 12,
    marginBottom: 10,
    paddingHorizontal: 2,
  },
  recentSectionTitle: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
    marginBottom: 4,
  },
  recentSectionSubtitle: {
    color: '#5f7e5a',
    fontWeight: '400',
    textTransform: 'none',
    letterSpacing: 0,
  },
  recentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  recentRowText: {
    flex: 1,
    minWidth: 0,
  },
  recentRowMeta: {
    color: '#7FA978',
    fontSize: 11,
    textAlign: 'right',
  },
  pickerDividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 8,
    marginVertical: 10,
    paddingHorizontal: 2,
  },
  pickerDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A3A20',
  },
  pickerDividerText: {
    color: '#5f7e5a',
    fontSize: 12,
  },
  nearbyLabel: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    paddingHorizontal: 2,
    marginBottom: 4,
  },
  courseOption: {
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  courseOptionActive: {
    backgroundColor: '#1A3A20',
  },
  courseOptionTitle: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  courseOptionSubtitle: {
    color: '#A1D493',
    fontSize: 12,
  },
  courseBadge: {
    marginTop: 4,
    alignSelf: 'flex-start',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  courseBadgeOfficial: {
    color: '#06280F',
    backgroundColor: '#A1D493',
  },
  courseBadgePrivate: {
    color: '#FCD34D',
    backgroundColor: 'rgba(252,211,77,0.15)',
  },
  courseBadgePending: {
    color: '#93C5FD',
    backgroundColor: 'rgba(147,197,253,0.15)',
  },
  separator: {
    height: 1,
    backgroundColor: '#1A3A20',
  },
  emptyState: {
    paddingVertical: 12,
    textAlign: 'center',
    color: '#C8C8B0',
  },
  addCourseButton: {
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0B2A14',
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  addCourseButtonText: {
    color: '#A1D493',
    fontWeight: '700',
  },
});
